export default class HeroAnimation {
  constructor(selector = '.hero-animation') {
    this.container = document.querySelector(selector)
    if (!this.container) {
      return
    }
    this.getRandom = this.seededRandom(window.location.pathname)
  }

  seededRandom(str) {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i)
      hash = hash & hash
    }
    return () => {
      hash = (hash * 16807) % 2147483647
      return (hash - 1) / 2147483646
    }
  }

  generatePositions() {
    const getDistanceBetweenPoints = (p1, p2) => {
      return Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2))
    }

    // Calculate safe boundaries to ensure squares stay fully within the 2:1 aspect ratio container
    // Square size is 150x150, using viewBox -300 -150 600 300
    // Adding more conservative padding to guarantee squares stay fully visible
    const squareHalfWidth = 75  // 150/2
    const safeZone = {
      minX: -300 + squareHalfWidth + 20, // Increase safety margin
      maxX: 300 - squareHalfWidth - 20,  // Increase safety margin
      minY: -150 + squareHalfWidth + 15, // Vertical space is more limited
      maxY: 150 - squareHalfWidth - 15
    }

    const generateSpacedPosition = (existingPoints, isStart) => {
      const minDistance = isStart ? 100 : 120 // Increased end position minimum distance
      const maxAttempts = 100 // Increased to find better positions
      let attempts = 0

      while (attempts < maxAttempts) {
        let point

        if (isStart) {
          // For start positions, use the seeded random to determine base positions
          const index = existingPoints.length
          const baseX = (this.getRandom() * 400 - 200) // Random position between -200 and 200
          const baseY = (this.getRandom() * 200 - 100) // Random position between -100 and 100

          // Add a small offset for subtle movement
          const subtleOffset = 40
          point = {
            x: baseX + (this.getRandom() * subtleOffset - subtleOffset/2),
            y: baseY + (this.getRandom() * subtleOffset - subtleOffset/2)
          }
        } else {
          // For end positions, use the seeded random to determine final positions
          const index = existingPoints.length
          const baseX = (this.getRandom() * 400 - 200) // Random position between -200 and 200
          const baseY = (this.getRandom() * 200 - 100) // Random position between -100 and 100

          // Add controlled randomness within the section
          const randomOffsetX = 15
          const randomOffsetY = 20
          point = {
            x: baseX + (this.getRandom() * randomOffsetX - randomOffsetX/2),
            y: baseY + (this.getRandom() * randomOffsetY - randomOffsetY/2)
          }
        }

        // Ensure points stay within safe boundaries
        point.x = Math.max(safeZone.minX, Math.min(safeZone.maxX, point.x))
        point.y = Math.max(safeZone.minY, Math.min(safeZone.maxY, point.y))

        const isFarEnough = existingPoints.every(existing =>
          getDistanceBetweenPoints(point, existing) >= minDistance
        )

        if (isFarEnough || attempts === maxAttempts - 1) {
          return point
        }

        attempts++
      }
    }

    const startPositions = []
    const endPositions = []

    return Array(3).fill().map(() => {
      const start = generateSpacedPosition(startPositions, true)
      startPositions.push(start)

      const end = generateSpacedPosition(endPositions, false)
      endPositions.push(end)

      const startScale = 0.8 + (this.getRandom() * 0.2)
      // Slightly reduce end scale to ensure better visibility
      const endScale = 0.95 + (this.getRandom() * 0.05)

      return {
        startX: start.x,
        startY: start.y,
        startRotation: this.getRandom() * 10 - 5, // Reduced rotation for subtlety
        startScale,
        endX: end.x,
        endY: end.y,
        endRotation: 0, // No rotation at the end, squares sit straight
        endScale,
      }
    })
  }

  createSVG() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    // Change viewBox to 2:1 aspect ratio (wider than tall)
    svg.setAttribute('viewBox', '-300 -150 600 300')
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet')
    svg.classList.add('hero-squares')
    return svg
  }

  createSquare(className, position) {
    const square = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
    // Center the rectangles by offsetting them by half their width/height
    square.setAttribute('width', '150')
    square.setAttribute('height', '150')
    square.setAttribute('x', '-75')  // -width/2 to center it
    square.setAttribute('y', '-75')  // -height/2 to center it
    square.setAttribute('rx', '10')
    square.classList.add(className, 'animated-square')

    // Set all the CSS custom properties
    Object.entries(position).forEach(([key, value]) => {
      square.style.setProperty(
        `--${key}`,
        typeof value === 'number'
          ? (key.includes('Rotation')
              ? `${value}deg`
              : key.includes('Scale')
                ? value
                : `${value}px`)
          : value,
      )
    })

    return square
  }

  init() {
    if (!this.container) return

    const svg = this.createSVG()
    const positions = this.generatePositions()

    const squareClasses = ['square-one', 'square-two', 'square-three']

    squareClasses.forEach((className, i) => {
      const square = this.createSquare(className, positions[i])
      svg.appendChild(square)
    })

    this.container.appendChild(svg)
  }
}
